export async function childrenReady(element: HTMLElement): Promise<void> {
  const { ownerDocument } = element;

  if (isDocumentReady(ownerDocument) || isNodeParsed(element)) {
    return;
  }

  const abortController = new AbortController();

  const documentReadyPromise = documentReady(ownerDocument, abortController.signal);
  const childsParsedPromise = childrenParsed(element, abortController.signal);

  await Promise.race([documentReadyPromise, childsParsedPromise]);

  abortController.abort();
}

async function documentReady(document: Document, signal?: AbortSignal): Promise<void> {
  if (isDocumentReady(document)) {
    return;
  }

  if (signal && signal.aborted) {
    return Promise.reject();
  }

  return new Promise<void>((resolve, reject) => {
    const handleStateChange = () => {
      if (isDocumentReady(document)) {
        document.removeEventListener('readystatechange', handleStateChange);

        resolve();
      }
    };

    document.addEventListener('readystatechange', handleStateChange);

    if (signal) {
      signal.addEventListener('abort', () => {
        document.removeEventListener('readystatechange', handleStateChange);

        reject();
      });
    }
  });
}

async function childrenParsed(element: HTMLElement, signal?: AbortSignal): Promise<void> {
  if (signal && signal.aborted) {
    return Promise.reject();
  }

  return new Promise<void>((resolve, reject) => {
    const observer = new MutationObserver(() => {
      if (isNodeParsed(element)) {
        observer.disconnect();
        resolve();
      }
    });

    observer.observe(element, { childList: true });

    if (signal) {
      signal.addEventListener('abort', () => {
        observer.disconnect();

        reject();
      });
    }
  });
}

function isDocumentReady(document: Document) {
  return document.readyState === 'interactive' || document.readyState === 'complete';
}

function isNodeParsed(node: Node) {
  let nextNode: Node | null = node;

  while (nextNode) {
    if (nextNode.nextSibling) {
      return true;
    }

    nextNode = nextNode.parentNode;
  }

  return false;
}
